<template>
	<v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable @keydown.esc="closeDialog">
		<v-card>
			<v-toolbar card dark color="primary">
				<v-btn icon dark @click.native="closeDialog">
					<v-icon>close</v-icon>
				</v-btn>
				<v-toolbar-title>{{ title }}</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-toolbar-items>
					<v-btn :loading="isSaving" :disabled="isSaving" dark flat @click="onSave">Save <span slot="loader">Saving...</span></v-btn>
				</v-toolbar-items>
			</v-toolbar>
			<v-card-text>
				<v-form>
					<v-container grid-list-xl fluid>
						<v-layout row wrap>
							<v-flex lg6>
								<v-card class="mb-4">
									<v-toolbar flat dense cad>
										<v-toolbar-title class="subheading">Core Information</v-toolbar-title>
										<v-spacer></v-spacer>
									</v-toolbar>
									<v-card-text>
										<v-text-field v-model="form.name" ref="name" :error-messages="nameErrors" label="Name"></v-text-field>
										<div v-if="!editAssetNo && form.assetNo != null && isEdit" class="text-xs-left">
											<span>Asset No: <h3 class="d-inline">{{ form.assetNo }}</h3><v-btn icon @click.prevent.stop="editAssetNo = true" class="ml-0"><v-icon small>edit</v-icon></v-btn></span>
										</div>
										<div v-else>
											<v-text-field v-if="!autoGenerateAssetNo" ref="assetNo" v-model="form.assetNo" :error-messages="assetNoErrors" label="Asset No" persistent-hint hint="The Asset No must be unique"></v-text-field>
											<v-checkbox v-if="!isEdit" v-model="autoGenerateAssetNo" label="Automatically generate unique Asset No"></v-checkbox>
										</div>
										<v-select v-model="form.categoryId" :items="categories" label="Category" item-text="name" item-value="id" clearable></v-select>
										<v-textarea v-model="form.description" :error-messages="descriptionErrors" label="Description"></v-textarea>
									</v-card-text>
								</v-card>
							</v-flex>
							<v-flex lg6>
								<v-card class="mb-4">
									<v-toolbar flat dense cad>
										<v-toolbar-title class="subheading">Extended Information</v-toolbar-title>
										<v-spacer></v-spacer>
									</v-toolbar>
									<v-card-text>
										<v-select v-model="form.currentLocationId" :items="locations" label="Location" item-text="name" item-value="id" clearable></v-select>
										<v-text-field v-model="form.manufacturer" :error-messages="manufacturerErrors" label="Manufacturer"></v-text-field>
										<v-text-field v-model="form.modelNo" :error-messages="modelNoErrors" label="Model No"></v-text-field>
										<v-text-field v-model="form.serialNo" :error-messages="serialNoErrors" label="Serial No"></v-text-field>
										<v-text-field v-model="form.vendor" :error-messages="vendorErrors" label="Vendor"></v-text-field>
										<v-text-field v-model="form.costPrice" :error-messages="costPriceErrors" type="number" prepend-inner-icon="mdi-currency-usd" label="Cost Price"></v-text-field>
										<v-menu
											ref="warrantyExpirationDateMenu"
											v-model="form.warrantyExpirationDate.menu"
											:close-on-content-click="false"
											:nudge-right="40"
											:return-value.sync="form.warrantyExpirationDate.date"
											lazy
											transition="scale-transition"
											offset-y
											full-width
											min-width="290px"
										>
											<v-text-field
												prepend-icon="date_range"
												slot="activator"
												v-model="warrantExpirationDateFormatted"
												clearable
												label="Warranty Expiration Date"
												readonly
												:error-messages="warrantyExpirationDateErrors"
											></v-text-field>
											<v-date-picker v-model="form.warrantyExpirationDate.date" @input="$refs.warrantyExpirationDateMenu.save(form.warrantyExpirationDate.date)"></v-date-picker>
										</v-menu>
										<v-menu
											ref="nextServiceDateMenu"
											v-model="form.nextServiceDate.menu"
											:close-on-content-click="false"
											:nudge-right="40"
											:return-value.sync="form.nextServiceDate.date"
											lazy
											transition="scale-transition"
											offset-y
											full-width
											min-width="290px"
										>
											<v-text-field
												prepend-icon="date_range"
												slot="activator"
												v-model="nextServiceDateFormatted"
												clearable
												label="Next Service Date"
												readonly
												:error-messages="nextServiceDateErrors"
											></v-text-field>
											<v-date-picker v-model="form.nextServiceDate.date" @input="$refs.nextServiceDateMenu.save(form.nextServiceDate.date)"></v-date-picker>
										</v-menu>
										<v-textarea v-model="form.notes" :error-messages="descriptionErrors" label="Notes"></v-textarea>
									</v-card-text>
								</v-card>
							</v-flex>
						</v-layout>
					</v-container>
				</v-form>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
	import AssetEditor from '@/components/assets/AssetEditor';
	import { mapGetters } from 'vuex';
	import { validationMixin } from 'vuelidate';
	import { required, requiredIf } from 'vuelidate/lib/validators';

	export default {
		components: {
			AssetEditor
		},
		mixins: [validationMixin],
		props: {
			dialog: {
				required: true
			},
			asset: {
				required: false,
			}
		},
		data() {
			return {
				form: null,
				autoGenerateAssetNo: true,
				editAssetNo: false,
				originalAssetNo: null
			};
		},
		computed: {
			...mapGetters({
				isSaving: 'assets/isSaving',
				categories: 'assetConfiguration/categories',
				locations: 'assetConfiguration/locations'
			}),
			title() {
				return this.asset != null ? 'Edit Asset' : 'Create Asset';
			},
			assetNoErrors() {
				const errors = [];
				if (!this.$v.form.assetNo.$dirty) return errors;
				!this.$v.form.assetNo.required && errors.push('Asset No is required.');
				return errors;
			},
			nameErrors() {
				const errors = [];
				if (!this.$v.form.name.$dirty) return errors;
				!this.$v.form.name.required && errors.push('Name is required.');
				return errors;
			},
			serialNoErrors() {
				return [];
			},
			manufacturerErrors() {
				return [];
			},
			modelNoErrors() {
				return [];
			},
			vendorErrors() {
				return [];
			},
			costPriceErrors() {
				return [];
			},
			warrantyExpirationDateErrors() {
				return [];
			},
			nextServiceDateErrors() {
				return [];
			},
			descriptionErrors() {
				return [];
			},
			groupIdErrors() {
				return [];
			},
			warrantExpirationDateFormatted: {
				get() {
					return this.$options.filters.formatDate(this.form.warrantyExpirationDate.date);
				},
				set() {
					this.form.warrantyExpirationDate.date = null;
					this.form.warrantyExpirationDate.menu = false;
				}
			},
			nextServiceDateFormatted: {
				get() {
					return this.$options.filters.formatDate(this.form.nextServiceDate.date);
				},
				set() {
					this.form.nextServiceDate.date = null;
					this.form.nextServiceDate.menu = false;
				}
			},
			isEdit() {
				return this.asset != null;
			}
		},
		watch: {
			dialog(newVal) {
				if (newVal)
					this.$nextTick(() => this.$refs.name ? this.$refs.name.focus() : null)
			},
			asset: {
				immediate: true,
				deep: true,
				handler(newVal) {
					if (!newVal) {
						this.form = this.getBlankForm()
						return;
					}

					const newForm  = {...this.getBlankForm(), ...newVal};

					// Here we have some funky mapping because this watcher is called after inital render
					// Which means we end up in a state where we have an object (from our component model)
					// And also the actual asset model (a string)
					if (typeof(newVal.warrantyExpirationDate) === "string" || !newVal.warrantyExpirationDate) {
						newForm.warrantyExpirationDate = {
							date: this.$options.filters.formatDate(newVal.warrantyExpirationDate, 'YYYY-MM-DD'),
							menu: false
						}
					}

					if (typeof(newVal.nextServiceDate) === "string" || !newVal.nextServiceDate) {
						newForm.nextServiceDate = {
							date: this.$options.filters.formatDate(newVal.nextServiceDate, 'YYYY-MM-DD'),
							menu: false
						}
					}
					
					this.form = newForm;
					
					this.originalAssetNo = newVal.assetNo;
					this.autoGenerateAssetNo = !this.isEdit
				}
			},
			autoGenerateAssetNo(newVal) {
				if (!newVal) {
					this.form.assetNo = this.originalAssetNo;
					this.$nextTick(() => this.$refs.assetNo ? this.$refs.assetNo.focus() : null);
				} else {
					this.form.assetNo = null;
				}
			}
		},
		methods: {
			async onSave() {
				this.$v.form.$touch();

				if (!this.$v.form.$invalid) {
					const savePayload = {
						...this.form,
						warrantyExpirationDate: this.form.warrantyExpirationDate.date,
						nextServiceDate: this.form.nextServiceDate.date
					};

					if (this.isEdit) {
						const result = await this.$store.dispatch('assets/update', savePayload);
						if (result.isSuccess) {
							this.closeDialog();
						}
					} else {
						const result = await this.$store.dispatch('assets/create', savePayload);
						if (result.isSuccess) {
							this.$router.push({ name: 'asset', params: { id: result.data.id } })
							this.closeDialog();
						}
					}
				}
			},
			closeDialog() {
				this.form = this.getBlankForm();
				this.autoGenerateAssetNo = true;
				this.$emit('closeDialog');
			},
			getBlankForm() {
				return {
					id: null,
					assetNo: null,
					name: null,
					groupId: null,
					description: null,
					manufacturer: null,
					modelNo: null,
					vendor: null,
					costPrice: null,
					currentLocationId: null,
					categoryId: null,
					warrantyExpirationDate: {
						date: null,
						menu: false
					},
					nextServiceDate: {
						date: null,
						menu: false
					},
					notes: null
				}
			}
		},
		validations: {
			form: {
				name: { required },
				assetNo: {
					required: requiredIf(function() {
						return !this.autoGenerateAssetNo;
					})
				}
			}
		}
	};
</script>
